import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { useEffect } from 'react';

import styles from './SidebarOverlay.module.css';

import { IconButton } from '@/shared/components/atoms/IconButton';
import { useMountTransition } from '@/shared/components/atoms/SidebarOverlay/logic';
import { ISideBarOverlayProps } from '@/shared/components/atoms/SidebarOverlay/SideBarOverlay';
import { Typography } from '@/shared/components/atoms/Typography';

import CloseIcon from '~/images/shared/Close.svg';

const SideBarOverlay = ({
  children,
  closeModal,
  containerClassName,
  className,
  title,
}: ISideBarOverlayProps) => {
  const { i18n } = useTranslation();

  const [open, setOpen] = useState(true);

  const DELAY = 300;

  //  INFO: delay unmount for transition
  const hasTransitioned = useMountTransition(open, DELAY);

  //  INFO: delay parent component unmount until the unmount transition has finished
  useEffect(() => {
    if (!open) {
      const timeoutId = setTimeout(() => {
        closeModal();
      }, DELAY);
      return () => clearTimeout(timeoutId);
    }
  }, [open, closeModal]);

  const overlayClasses = classNames(
    'fixed inset-0 z-50 overflow-auto bg-black bg-opacity-5 flex overflow-x-hidden justify-end',
    className
  );

  const containerClasses = classNames(
    'h-full w-[65vw] overflow-y-auto bg-background-neutral-100 py-32 px-24 transition-all ease-in-out duration-300 scrollbar flex flex-col',
    i18n.dir() === 'rtl'
      ? 'rounded-tr-2xl rounded-br-2xl'
      : 'rounded-tl-2xl rounded-bl-2xl',
    styles['hidden'],
    styles[i18n.dir()],
    hasTransitioned && styles['in'],
    open && styles['visible'],
    containerClassName
  );

  return (
    <div className={overlayClasses} onClick={() => setOpen(false)}>
      {(hasTransitioned || open) && (
        <div className={containerClasses} onClick={(e) => e.stopPropagation()}>
          <div className='mb-32 flex items-center justify-between'>
            {title && typeof title === 'string' ? (
              <Typography variant='h2' weight='bold'>
                {title}
              </Typography>
            ) : (
              title
            )}
            <IconButton
              className='h-20 w-20 text-primary-default'
              onClick={() => setOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </div>
          {children}
        </div>
      )}
    </div>
  );
};

export default SideBarOverlay;
